import { initCart } from "./cart.js";
import { UUID } from "./uuid.js";
// import { initAnalytics, logNavigation } from "./analytics.js";

export function initRouter(addToCart) {
  const routerEvent = new Event("mvthru-router");

  return new Promise((resolve) => {
    // Log Arrival Path
    // logNavigation("/");

    // Important States
    let currentRoute = null;
    let latestPath = null;
    const pathsBeingFetched = new Set();
    let observer;

    let loading = {
      bar: null,
      progress: 0,
      interval: null,
    };

    // Core Router
    async function router() {
      const path = window.location.pathname || "/";
      const queryString = window.location.search || "";
      const fullPath = path + queryString; // Include query string in the route

      currentRoute = fullPath;
      try {
        latestPath = fullPath;
        await updateContent(fullPath);
      } catch (error) {
        console.error("Failed to fetch content:", error);
      } finally {
        if (latestPath === fullPath) {
          completeLoading();
        }
      }
      attachEventListeners();
    }

    // Loading Bar Functions
    function createLoadingBar() {
      if (!loading.bar) {
        loading.bar = document.createElement("div");
        loading.bar.id = "loading-bar";
        loading.bar.style.cssText = `
          position: fixed; top: 0; left: 0; height: 3px; width: 0; background: var(--mvthru-foreground); transition: width 0.2s ease-in-out; z-index: 9999;
        `;
        document.body.appendChild(loading.bar);
      }
    }

    function startLoading() {
      clearInterval(loading.interval);
      if (loading.bar) {
        loading.bar.remove();
      }
      loading.bar = null;
      loading.progress = 0;

      createLoadingBar();
      loading.interval = setInterval(() => {
        if (loading.progress < 95) {
          loading.progress += Math.random() * 5;
          updateLoadingBar();
        }
      }, 200);
    }

    function updateLoadingBar() {
      if (loading.bar) {
        loading.bar.style.width = loading.progress + "%";
      }
    }

    function completeLoading() {
      clearInterval(loading.interval);
      loading.progress = 100;
      updateLoadingBar();
      setTimeout(() => {
        if (loading.bar) {
          loading.bar.remove();
          loading.bar = null;
        }
        loading.progress = 0;
      }, 200);
    }

    // Cache & Content Fetching
    function isCacheExpired(headers) {
      let isExpired = false;

      const cacheControl = headers.get("Cache-Control");
      const expires = headers.get("Expires");

      if (cacheControl) {
        const maxAgeMatch = cacheControl.match(/max-age=(\d+)/);

        if (maxAgeMatch) {
          const maxAge = parseInt(maxAgeMatch[1], 10);
          const timeFetched = headers.get("Date");
          if (timeFetched) {
            const timeFetchedInSeconds = Date.parse(timeFetched) / 1000;
            const timeNowInSeconds = Date.now() / 1000;

            const age = timeNowInSeconds - timeFetchedInSeconds;

            if (age > maxAge) {
              isExpired = true;
            }
          }
        } else if (
          cacheControl.includes("no-store") ||
          cacheControl.includes("no-cache")
        ) {
          isExpired = true;
        }
      }

      if (!isExpired && expires) {
        const expiresDate = new Date(expires);
        if (new Date() > expiresDate) {
          isExpired = true;
        }
      }

      return isExpired;
    }

    async function fetchContent(fullPath, isPrefetch = false) {
      // Check if the path is already being fetched
      if (pathsBeingFetched.has(fullPath)) {
        return; // Exit if already fetching
      }

      pathsBeingFetched.add(fullPath); // Add full path to active fetch paths
      const cache = await caches.open("route-cache");

      try {
        const cachedResponse = await cache.match(fullPath);
        let headers = {};

        // If there's cached content, add validation headers
        if (cachedResponse) {
          const eTag = cachedResponse.headers.get("ETag");
          const lastModified = cachedResponse.headers.get("Last-Modified");

          if (eTag) headers["If-None-Match"] = eTag;
          if (lastModified) headers["If-Modified-Since"] = lastModified;
        }

        // Split the path for API request
        const [basePath, queryString] = fullPath.split("?");
        const apiPath = `/api/route${basePath}${queryString ? `?${queryString}` : ""}`;

        const response = await fetch(apiPath, {
          headers,
        });

        // If 403, refresh because likely preview token as expired
        if (response.status === 403) {
          location.reload();
        }

        // If the response is 304, the cached content is still valid
        if (response.status === 304 && cachedResponse) {
          console.log(`[MvThru] Content not modified for ${fullPath}`);
          pathsBeingFetched.delete(fullPath);
          return; // Skip replacing cache or rendering
        }

        // If the response is OK, update the cache and render
        if (response.ok) {
          const clonedResponse = response.clone();
          await cache.put(fullPath, clonedResponse);
          const content = await response.text();

          if (!isPrefetch || currentRoute === fullPath) {
            renderContent(content);
            updateMetadata(fullPath);
          }
        } else {
          throw new Error(`Failed to fetch content for ${fullPath}`);
        }
      } catch (error) {
        console.error(`Error fetching content for ${fullPath}:`, error);
      } finally {
        pathsBeingFetched.delete(fullPath); // Remove path from active fetch paths
      }
    }

    // Also modify updateContent to handle query parameters
    async function updateContent(fullPath) {
      const cache = await caches.open("route-cache");
      const cachedResponse = await cache.match(fullPath);

      if (cachedResponse) {
        const isStale = true;
        const cachedContent = await cachedResponse.text();

        // Serve cached content immediately
        renderContent(cachedContent);
        updateMetadata(fullPath);

        // If the cache is stale, fetch new content in the background
        if (isStale) {
          console.log(
            `[MvThru] Cache stale for ${fullPath}. Fetching updated content.`,
          );
          fetchContent(fullPath, true);
        }
      } else {
        // No cached content, fetch directly from the network
        console.log(
          `[MvThru] No cached content for ${fullPath}. Fetching from network.`,
        );
        startLoading();
        await fetchContent(fullPath);
        completeLoading();
      }
    }

    // Include the renderContent fix from before to ensure data scripts update correctly
    function renderContent(content) {
      const contentContainer = document.querySelector(
        '[data-mvthru-view="content"]',
      );

      // First, extract any script tags with data- attributes from the new content
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = content;

      // Find all data scripts in the new content
      const dataScripts = tempDiv.querySelectorAll(
        'script[id^="mvthru-data-"]',
      );

      // Update or add these data scripts to the document head
      dataScripts.forEach((script) => {
        const scriptId = script.getAttribute("id");
        const existingScript = document.getElementById(scriptId);

        if (existingScript) {
          // Replace existing script with new one
          existingScript.textContent = script.textContent;
        } else {
          // Clone and add the script to document head
          const newScript = document.createElement("script");
          newScript.id = scriptId;
          newScript.type = script.type || "application/json";
          newScript.textContent = script.textContent;
          document.head.appendChild(newScript);
        }

        // Remove the script from the content to avoid duplication
        script.parentNode.removeChild(script);
      });

      // Now update the main content
      contentContainer.innerHTML = tempDiv.innerHTML;
    }

    async function navigate(path) {
      // Extract base path and query parameters
      const [basePath, queryString] = path.split("?");
      const fullPath = path; // Keep the full path including query params

      window.history.pushState({}, "", fullPath);
      await router();
      window.scrollTo(0, 0);
      document.dispatchEvent(routerEvent);

      // Temp - Init cart on /cart
      if (basePath === "/cart") {
        initCart();
      }
    }

    function renderContent(content) {
      document.querySelector('[data-mvthru-view="content"]').innerHTML =
        content;
    }

    function updateMetadata(path) {
      const store = grabClientSideData("store");

      if (path === "/") {
        document.title = `${store.settings.name}`;
      } else if (path === "/products") {
        document.title = `All Products | ${store.settings.name}`;
      } else if (path.startsWith("/product")) {
        // Check for Product Data
        const product = grabClientSideData("product");
        if (product) {
          document.title =
            product.metadata?.title ||
            `${product.title} | ${store.settings.name}`;
        }
      } else if (path.startsWith("/page")) {
        // Check for Product Data
        const product = grabClientSideData("page");
        if (product) {
          document.title =
            product.metadata?.title ||
            `${product.title} | ${store.settings.name}`;
        }
      }
    }

    function attachEventListeners() {
      document.addEventListener("click", handleClick);
      document.addEventListener("submit", handleFormSubmit);
      observeLinks();
    }

    function handleClick(e) {
      const anchor = e.target.closest("a[href^='/']");

      if (anchor) {
        const path = anchor.getAttribute("href");

        // Ignore Clicks During/After Checkout
        if (
          !window.location.pathname.startsWith("/checkout") &&
          !path.startsWith("/checkout")
        ) {
          e.preventDefault();
          navigate(path); // Pass the full path including query params
        }
      }
    }

    function grabClientSideData(tag) {
      const productDataScript = document.getElementById(`mvthru-data-${tag}`);
      console.log("Grabbing", tag);
      if (productDataScript) {
        try {
          return JSON.parse(productDataScript.textContent);
        } catch (e) {
          console.error("Failed to parse mvthru product data:", e);
          return null;
        }
      }
      return null;
    }

    async function handleFormSubmit(e) {
      if (e.target.tagName.toLowerCase() === "form") {
        e.preventDefault();
        const form = e.target;
        const submitButton = form.querySelector(
          'input[type="submit"], button[type="submit"]',
        );
        const originalButtonText =
          submitButton.textContent || submitButton.value;
        submitButton.disabled = true;

        const buttonHeight = submitButton.offsetHeight;
        submitButton.style.height = `${buttonHeight}px`;
        submitButton.style.position = "relative";

        submitButton.innerHTML = `
        <div style="position: absolute; top: 50%; left: 50%; width: 20px; height: 20px; margin: -10px 0 0 -10px; border: 4px solid rgba(255, 255, 255, 0.2); border-top: 4px solid #fff; border-radius: 50%; animation: spin 1s linear infinite;"></div>
        <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        </style>
      `;

        const formData = new FormData(form);

        if (form.getAttribute("data-mvthru-form-action") === "add-to-cart") {
          const product = grabClientSideData("product");

          // If no variant, and only one in data, grab it.
          if (!formData.has("variant")) {
            if (product.variants.length === 1) {
              formData.set("variant", product.variants[0].id);
            } else {
              console.log(
                "[MvThru] This product has multiple variants, you must specify which one to add.",
              );
            }
          }

          // Grab variant from client-side data
          const variant = product.variants.find(
            (item) => item.id === formData.get("variant"),
          );

          // Create UUID incase of new item
          const newUUID = UUID.generate();
          formData.set("id", newUUID);

          addToCart({
            id: newUUID,
            product,
            variant,
            quantity: parseInt(formData.get("quantity"), 10) ?? 1,
          });
        }

        try {
          const response = await fetch(form.action, {
            method: form.method.toLowerCase(),
            body: form.method === "post" ? formData : undefined,
          });
          if (!response.ok) throw new Error("Submission failed");
          const result = await response.json();
        } catch (error) {
          console.error("Error submitting form:", error);
        }
        submitButton.disabled = false;
        submitButton.textContent = originalButtonText;
      }
    }

    // Prefetch Client-Side Links
    async function observeLinks() {
      if (observer) observer.disconnect();

      observer = new IntersectionObserver(
        async (entries) => {
          const visibleLinks = entries
            .filter((entry) => entry.isIntersecting)
            .map((entry) => entry.target.getAttribute("href"))
            .filter(Boolean);

          const cache = await caches.open("route-cache");

          for (const link of visibleLinks) {
            const cachedResponse = await cache.match(link);

            if (!cachedResponse || isCacheExpired(cachedResponse.headers)) {
              fetchContent(link, true);
            }
          }
        },
        { rootMargin: "200px" },
      );

      document
        .querySelector('[data-mvthru-view="content"]')
        .querySelectorAll('a[href^="/"]')
        .forEach((anchor) => {
          observer.observe(anchor);
        });
    }

    // Reattach on Back/Forward Buttons
    window.addEventListener("popstate", () => router());

    // window.addEventListener("popstate", (event) => {
    //   const scrollPos = event.state?.scrollPos || 0;
    //   window.scrollTo(0, scrollPos);
    // });

    // When DOM Loaded, Attach Link/Form Listeners
    document.addEventListener("DOMContentLoaded", () => {
      attachEventListeners();
    });

    // Prefetch Server-Side Links
    document.querySelectorAll('header a[href^="/"]').forEach(async (anchor) => {
      const link = anchor.getAttribute("href");
      const cache = await caches.open("route-cache");
      const cachedResponse = await cache.match(link);

      if (!cachedResponse || isCacheExpired(cachedResponse.headers)) {
        fetchContent(link, true);
      }
    });

    resolve();
  });
}
